import clsx from "clsx";
import { type KeyboardEvent, type ReactNode } from "react";
import { CC_LIST_ITEM_HOVER, CC_LIST_ITEM_INNER, CC_PANEL } from "@/components/case-checking/caseCheckingChrome";

type CaseCheckingListRowProps = {
  onClick?: () => void;
  testId?: string;
  children: ReactNode;
  footer?: ReactNode;
  variant?: "panel" | "flush";
  as?: "div" | "li";
  className?: string;
  innerClassName?: string;
  ariaExpanded?: boolean;
  /** Required when row content does not expose a clear accessible name. */
  ariaLabel?: string;
};

function handleRowKeyDown(onClick: () => void, event: KeyboardEvent<HTMLElement>) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onClick();
  }
}

export function CaseCheckingListRow({
  onClick,
  testId,
  children,
  footer,
  variant = "panel",
  as = "div",
  className,
  innerClassName,
  ariaExpanded,
  ariaLabel,
}: CaseCheckingListRowProps) {
  const interactive = Boolean(onClick);
  const Component = as;

  const headerClass = clsx(
    CC_LIST_ITEM_INNER,
    interactive && "cursor-pointer",
    interactive && CC_LIST_ITEM_HOVER,
    innerClassName,
  );

  if (variant === "panel") {
    return (
      <Component
        className={clsx(CC_PANEL, "overflow-hidden p-0", className)}
        data-testid={testId}
      >
        <div
          role={interactive ? "button" : undefined}
          tabIndex={interactive ? 0 : undefined}
          onClick={onClick}
          onKeyDown={interactive && onClick ? (e) => handleRowKeyDown(onClick, e) : undefined}
          aria-expanded={ariaExpanded}
          aria-label={interactive ? ariaLabel : undefined}
          className={headerClass}
        >
          {children}
        </div>
        {footer}
      </Component>
    );
  }

  return (
    <Component className={className} data-testid={testId}>
      <div
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={interactive && onClick ? (e) => handleRowKeyDown(onClick, e) : undefined}
        aria-expanded={ariaExpanded}
        aria-label={interactive ? ariaLabel : undefined}
        className={headerClass}
      >
        {children}
      </div>
      {footer}
    </Component>
  );
}
